import React from 'react';
import { fmtMoney } from './format';
import Panel from './Panel';
import { arOf, countedTimeEntries, issuedOnly } from '../../utils/rollup';

const th = {
  padding: '12px 8px', textAlign: 'right', fontWeight: 600, fontSize: 11,
  color: '#374151', letterSpacing: '0.05em',
};
const thLeft = { ...th, textAlign: 'left' };
const td = { padding: '12px 8px', fontSize: 14, textAlign: 'right' };

/**
 * Design spec §3.3: every cell in this table click-throughs to its raw rows,
 * not just the client-name cell. This component owns no money arithmetic —
 * `rows` (the per-client totals) already come from rollup.js's byClient(),
 * and the raw `timeEntries` / `invoices` / `writeOffs` passed in here are
 * only ever FILTERED (by client id, by counted status, issued-not-draft, unpaid —
 * the same filters SummaryByYearTable already applies for its own drill-down
 * and A/R column), never summed or recomputed.
 */
export default function BreakdownByClientTable({
  rows, onClientClick, onCellClick, timeEntries, invoices, writeOffs,
}) {
  const rowsForClient = (clientId) => {
    const own = countedTimeEntries(timeEntries).filter((t) => t.client === clientId);
    const inv = issuedOnly(invoices).filter((i) => i.client === clientId);
    const wo = (writeOffs || []).filter((w) => w.client === clientId);
    return { timeEntries: own, invoices: inv, writeOffs: wo };
  };

  const cell = (value, color, drillRows, metric, kind, clientName) => {
    const clickable = onCellClick && drillRows && drillRows.length > 0;
    return (
      <td
        style={{ ...td, color, cursor: clickable ? 'pointer' : 'default' }}
        onClick={clickable ? () => onCellClick({ metric, kind, rows: drillRows, clientName }) : undefined}
      >
        {value}
      </td>
    );
  };

  return (
    <Panel title="Breakdown by Client" empty={!rows?.length} emptyText="No clients in this group">
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={thLeft}>CLIENT</th>
              <th style={th}>HOURS</th>
              <th style={th}>TIME</th>
              <th style={th}>INVOICED</th>
              <th style={th}>WRITE-OFFS</th>
              <th style={th}>WIP</th>
              <th style={th}>A/R</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => {
              const own = rowsForClient(r.id);
              const arInvoices = own.invoices.filter((i) => arOf(i) !== 0);
              return (
                <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td
                    style={{
                      ...td, textAlign: 'left', fontWeight: 600, color: '#2563eb',
                      cursor: onClientClick ? 'pointer' : 'default',
                    }}
                    onClick={onClientClick ? () => onClientClick(r.id) : undefined}
                  >
                    {r.name}
                  </td>
                  {cell(r.hours.toFixed(2), undefined, own.timeEntries, 'hours', 'time', r.name)}
                  {cell(fmtMoney(r.time), '#059669', own.timeEntries, 'time', 'time', r.name)}
                  {cell(fmtMoney(r.invoiced), '#059669', own.invoices, 'invoiced', 'invoice', r.name)}
                  {cell(fmtMoney(r.writeoffs), '#dc2626', own.writeOffs, 'writeoffs', 'writeoff', r.name)}
                  <td
                    style={{ ...td, color: r.wip < 0 ? '#dc2626' : '#f59e0b', cursor: onCellClick ? 'pointer' : 'default' }}
                    onClick={onCellClick ? () => onCellClick({
                      metric: 'wip',
                      kind: 'wip',
                      rows: { timeEntries: own.timeEntries, invoices: own.invoices, writeOffs: own.writeOffs },
                      clientName: r.name,
                    }) : undefined}
                  >
                    {fmtMoney(r.wip)}
                  </td>
                  {cell(fmtMoney(r.ar), '#7c3aed', arInvoices, 'ar', 'invoice', r.name)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
